import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import HubPageShell from '../components/home/HubPageShell';
import PreferencePicker from '../components/preferences/PreferencePicker';
import { useToast } from '../contexts/ToastContext';
import { addNewsWatchlistItem, getNewsWatchlist, removeNewsWatchlistItem } from '../api/newsWatchlist';
import {
  CITY_OPTIONS_BY_COUNTRY,
  COMPANY_SCALE_OPTIONS,
  COUNTRY_OPTIONS,
  EMPTY_PREFERENCES,
  EVENT_TYPE_OPTIONS,
  INDUSTRY_OPTIONS,
  LIMITS,
  MARKET_OPTIONS,
  SUGGESTED_EVENT_TYPES,
  SUGGESTED_INDUSTRIES,
  SUGGESTED_INTERESTS,
  SUGGESTED_MARKETS,
  SUGGESTED_ORGANISATIONS,
  SUGGESTED_TECHNOLOGIES,
  SUGGESTED_WATCHLIST,
  TECHNOLOGY_OPTIONS,
  formatPlace,
  mergePreferences,
  parseInterestTags,
  serializeInterestTags,
} from '../utils/insightPreferences';
import { refreshNearMeIfNeeded } from '../utils/eventLocation';
import { refreshCurrentMarketIfNeeded } from '../utils/marketLocation';
import styles from './InsightPreferencesPage.module.css';

function Switch({ checked, onChange, disabled, labelledBy }) {
  return (
    <button
      type="button"
      className={styles.switch}
      role="switch"
      aria-checked={checked}
      aria-labelledby={labelledBy}
      disabled={disabled}
      onClick={() => onChange(!checked)}
    >
      <span className={styles.switchThumb} aria-hidden="true" />
    </button>
  );
}

function formatWatchlistSummary(items) {
  const n = items.length;
  if (n === 0) return 'Nothing monitored yet';
  if (n === 1) return '1 organisation monitored';
  return `${n} organisations monitored`;
}

export default function InsightPreferencesPage() {
  const { showSuccess, showError } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [preferences, setPreferences] = useState(EMPTY_PREFERENCES);
  const [saveState, setSaveState] = useState('idle');
  const [watchlistItems, setWatchlistItems] = useState([]);
  const [watchlistMax, setWatchlistMax] = useState(40);
  const [watchlistLoading, setWatchlistLoading] = useState(false);
  const [watchlistSaving, setWatchlistSaving] = useState(false);
  const [weeklyDigestEmailEnabled, setWeeklyDigestEmailEnabled] = useState(false);
  const [weeklyEmailSaving, setWeeklyEmailSaving] = useState(false);
  const [newsAlertsEmailEnabled, setNewsAlertsEmailEnabled] = useState(false);
  const [newsAlertsSaving, setNewsAlertsSaving] = useState(false);
  const [eventPlace, setEventPlace] = useState({ city: '', country: '', lastUpdated: null });
  const [geoStatus, setGeoStatus] = useState('idle');
  const [marketGeoStatus, setMarketGeoStatus] = useState('idle');

  const saveTimer = useRef(null);
  const pendingPrefs = useRef(null);
  const saveGeneration = useRef(0);

  const flushSave = useCallback(async () => {
    const next = pendingPrefs.current;
    if (!next) return;
    const gen = ++saveGeneration.current;
    setSaveState('saving');
    try {
      const payload = { ...next };
      delete payload.jobRoles;
      await api.post('/api/user/insight-preferences', { preferences: payload });
      if (gen === saveGeneration.current) setSaveState('saved');
    } catch (error) {
      console.error('Error saving insight preferences:', error);
      if (gen === saveGeneration.current) {
        setSaveState('error');
        showError('Could not save preferences. Try again.');
      }
    }
  }, [showError]);

  const queueSave = useCallback(
    (next) => {
      pendingPrefs.current = next;
      setSaveState('saving');
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => {
        flushSave();
      }, 420);
    },
    [flushSave]
  );

  useEffect(
    () => () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    },
    []
  );

  const fetchPreferences = useCallback(async () => {
    setIsLoading(true);
    setLoadError('');
    try {
      const response = await api.get('/api/user/insight-preferences');
      const merged = mergePreferences(response.data?.preferences);
      setPreferences(merged);
      pendingPrefs.current = merged;
      const loc = response.data?.eventLocation || {};
      setEventPlace({
        city: loc.city || '',
        country: loc.country || '',
        lastUpdated: loc.lastUpdated || null,
      });
      if (merged.locationPreferences?.followCurrentMarket) {
        const marketRefresh = await refreshCurrentMarketIfNeeded({ preferences: merged, force: false });
        if (marketRefresh.status === 'ok' && marketRefresh.country) {
          const withCountry = {
            ...merged,
            locationPreferences: {
              ...merged.locationPreferences,
              currentMarketCountry: marketRefresh.country,
              currentMarketUpdatedAt: marketRefresh.currentMarketUpdatedAt || merged.locationPreferences.currentMarketUpdatedAt,
            },
          };
          setPreferences(withCountry);
          pendingPrefs.current = withCountry;
        }
      }
    } catch (error) {
      console.error('Error fetching preferences:', error);
      setLoadError('Could not load your preferences.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchWatchlist = useCallback(async () => {
    setWatchlistLoading(true);
    try {
      const data = await getNewsWatchlist();
      setWatchlistItems(data?.items || []);
      if (typeof data?.maxItems === 'number') setWatchlistMax(data.maxItems);
    } catch (error) {
      console.error('Watchlist load failed:', error);
    } finally {
      setWatchlistLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPreferences();
  }, [fetchPreferences]);

  useEffect(() => {
    fetchWatchlist();
  }, [fetchWatchlist]);

  useEffect(() => {
    (async () => {
      try {
        const ns = await api.get('/api/user/notification-settings');
        setWeeklyDigestEmailEnabled(ns.data.weeklyDigestEmailEnabled === true);
        setNewsAlertsEmailEnabled(ns.data.newsAlertsEmailEnabled === true);
      } catch {
        /* non-blocking */
      }
    })();
  }, []);

  const applyPreferences = (updater) => {
    setPreferences((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      queueSave(next);
      return next;
    });
  };

  const setList = (category, next, limit) => {
    applyPreferences((prev) => {
      const unique = [...new Set((next || []).map((v) => String(v).trim()).filter(Boolean))];
      if (typeof limit === 'number' && unique.length > limit) {
        showError(`Choose up to ${limit}.`);
        return prev;
      }
      return { ...prev, [category]: unique };
    });
  };

  const handleLimit = (limit) => {
    showError(`Choose up to ${limit}. Remove one to add another.`);
  };

  const requestCurrentLocation = useCallback(async ({ force = true } = {}) => {
    if (!navigator.geolocation) {
      setGeoStatus('unavailable');
      return;
    }
    setGeoStatus('requesting');
    const result = await refreshNearMeIfNeeded({
      preferences: { locationPreferences: { useCurrentLocation: true } },
      eventLocation: eventPlace,
      force,
    });
    if (result.eventLocation) {
      setEventPlace({
        city: result.eventLocation.city || '',
        country: result.eventLocation.country || '',
        lastUpdated: result.eventLocation.lastUpdated || new Date().toISOString(),
      });
    }
    if (result.status === 'updated' || result.status === 'fresh') setGeoStatus('granted');
    else if (result.status === 'denied') setGeoStatus('denied');
    else if (result.status === 'unavailable') setGeoStatus('unavailable');
    else setGeoStatus('error');
  }, [eventPlace]);

  const handleEventPlaceMode = (nearMe) => {
    applyPreferences((prev) => ({
      ...prev,
      locationPreferences: {
        ...prev.locationPreferences,
        useCurrentLocation: nearMe,
      },
    }));
    if (nearMe) requestCurrentLocation({ force: true });
  };

  const handleAddWatchlist = async (label) => {
    const value = String(label || '').trim();
    if (value.length < 2) {
      showError('Enter at least 2 characters.');
      return;
    }
    if (watchlistItems.length >= watchlistMax) {
      showError(`You can monitor up to ${watchlistMax} organisations.`);
      return;
    }
    if (watchlistItems.some((item) => String(item.label).toLowerCase() === value.toLowerCase())) {
      showError('That organisation is already on your watchlist.');
      return;
    }
    const tempId = `pending-${Date.now()}`;
    setWatchlistItems((prev) => [...prev, { id: tempId, label: value, category: 'company', priority: 'high' }]);
    setWatchlistSaving(true);
    try {
      await addNewsWatchlistItem({
        category: 'company',
        label: value,
        priority: 'high',
      });
      await fetchWatchlist();
      showSuccess('Added to your watchlist.');
    } catch (err) {
      setWatchlistItems((prev) => prev.filter((item) => item.id !== tempId));
      const msg = err.response?.data?.message || err.message;
      if (err.response?.status === 409) {
        showError('That organisation is already on your watchlist.');
      } else {
        showError(msg || 'Could not add organisation.');
      }
    } finally {
      setWatchlistSaving(false);
    }
  };

  const handleRemoveWatchlist = async (id) => {
    const previous = watchlistItems;
    setWatchlistItems((prev) => prev.filter((item) => item.id !== id));
    if (String(id).startsWith('pending-')) return;
    setWatchlistSaving(true);
    try {
      await removeNewsWatchlistItem(id);
    } catch {
      setWatchlistItems(previous);
      showError('Could not remove organisation.');
    } finally {
      setWatchlistSaving(false);
    }
  };

  const handleMonitorOrganisation = async (name) => {
    await handleAddWatchlist(name);
  };

  const handleWeeklyDigestEmailToggle = async (checked) => {
    setWeeklyDigestEmailEnabled(checked);
    setWeeklyEmailSaving(true);
    try {
      await api.post('/api/user/notification-settings', { weeklyDigestEmailEnabled: checked });
    } catch {
      setWeeklyDigestEmailEnabled(!checked);
      showError('Could not update email preference.');
    } finally {
      setWeeklyEmailSaving(false);
    }
  };

  const handleNewsAlertsEmailToggle = async (checked) => {
    setNewsAlertsEmailEnabled(checked);
    setNewsAlertsSaving(true);
    try {
      await api.post('/api/user/notification-settings', { newsAlertsEmailEnabled: checked });
    } catch {
      setNewsAlertsEmailEnabled(!checked);
      showError('Could not update alert preference.');
    } finally {
      setNewsAlertsSaving(false);
    }
  };

  const handleFollowCurrentMarketToggle = async (checked) => {
    applyPreferences((prev) => ({
      ...prev,
      locationPreferences: {
        ...prev.locationPreferences,
        followCurrentMarket: checked,
      },
    }));
    if (!checked) {
      setMarketGeoStatus('idle');
      return;
    }
    setMarketGeoStatus('requesting');
    const result = await refreshCurrentMarketIfNeeded({
      preferences: {
        ...preferences,
        locationPreferences: { ...preferences.locationPreferences, followCurrentMarket: true },
      },
      force: true,
    });
    if (result.status === 'ok' && result.country) {
      applyPreferences((prev) => ({
        ...prev,
        locationPreferences: {
          ...prev.locationPreferences,
          followCurrentMarket: true,
          currentMarketCountry: result.country,
          currentMarketUpdatedAt: result.currentMarketUpdatedAt || new Date().toISOString(),
        },
      }));
      setMarketGeoStatus('idle');
      return;
    }
    if (result.status === 'fresh') {
      setMarketGeoStatus('idle');
      return;
    }
    setMarketGeoStatus(result.status);
    if (result.status === 'denied') {
      showError('Location access was blocked. Markets will keep using your last known country if we have one.');
    } else if (result.status === 'unavailable') {
      showError('Current location is not available in this browser.');
    } else if (result.status === 'network') {
      showError('Could not reach Velox. Try again.');
    } else if (result.status === 'auth') {
      showError('Please sign in again, then try again.');
    } else {
      showError('Could not determine your country. Markets will keep using your last known country if we have one.');
    }
  };

  const handleRefreshCurrentMarket = async () => {
    setMarketGeoStatus('requesting');
    const result = await refreshCurrentMarketIfNeeded({
      preferences: {
        ...preferences,
        locationPreferences: { ...preferences.locationPreferences, followCurrentMarket: true },
      },
      force: true,
    });
    if (result.status === 'ok' && result.country) {
      applyPreferences((prev) => ({
        ...prev,
        locationPreferences: {
          ...prev.locationPreferences,
          followCurrentMarket: true,
          currentMarketCountry: result.country,
          currentMarketUpdatedAt: result.currentMarketUpdatedAt || new Date().toISOString(),
        },
      }));
      setMarketGeoStatus('idle');
      return;
    }
    setMarketGeoStatus(result.status === 'fresh' ? 'idle' : result.status);
    if (result.status !== 'ok' && result.status !== 'fresh') {
      showError('Could not update your current country.');
    }
  };

  const loc = preferences.locationPreferences || EMPTY_PREFERENCES.locationPreferences;
  const overrideCountry = loc.locationOverride?.country || '';
  const overrideCity = loc.locationOverride?.city || '';
  const cityChoices = CITY_OPTIONS_BY_COUNTRY[overrideCountry] || [];
  const useCurrentLocation = loc.useCurrentLocation !== false;
  const interestTags = parseInterestTags(preferences.customInterests);
  const watchlistLabels = new Set(watchlistItems.map((item) => String(item.label || '').toLowerCase()));
  const hasLegacyWatchItems = watchlistItems.some(
    (item) => item.category && item.category !== 'company' && item.category !== 'institution'
  );
  const nearMeLabel = formatPlace(eventPlace.city, eventPlace.country);
  const chosenLabel = formatPlace(overrideCity, overrideCountry);

  const statusLabel =
    saveState === 'saving' ? 'Saving' : saveState === 'saved' ? 'Saved' : saveState === 'error' ? 'Not saved' : '';

  let geoHint = 'Uses your current location when looking for events. The last area is cached for display, not treated as permanent.';
  if (geoStatus === 'requesting') geoHint = 'Finding your city…';
  else if (geoStatus === 'denied') geoHint = 'Location access was blocked. Events will use your last known area if we have one, or choose a city instead.';
  else if (geoStatus === 'unavailable') geoHint = 'Current location is not available in this browser. Choose a city instead.';
  else if (geoStatus === 'error') geoHint = 'Could not determine a city from your location. Choose a city instead.';
  else if (nearMeLabel) geoHint = `Events will use your current location. Last area: ${nearMeLabel}.`;

  return (
    <HubPageShell title="Insight preferences">
      <div className={styles.page}>
        <header className={styles.header}>
          <p className={styles.lead}>
            Choose what matters to you. Velox uses these preferences to tailor Market Intelligence, networking opportunities and briefings.
          </p>
          <div
            className={`${styles.status} ${
              saveState === 'saving' ? styles.statusSaving : saveState === 'saved' ? styles.statusSaved : saveState === 'error' ? styles.statusError : ''
            }`}
            aria-live="polite"
          >
            {statusLabel}
          </div>
        </header>

        {loadError ? (
          <div className={styles.error} role="alert">
            {loadError}
            <button type="button" className={styles.retry} onClick={fetchPreferences}>
              Try again
            </button>
          </div>
        ) : null}

        {isLoading ? (
          <div className={styles.skeleton} aria-busy="true">
            <div className={styles.skelBlock} />
            <div className={styles.skelBlock} />
            <div className={styles.skelBlock} />
          </div>
        ) : (
          <>
            <section className={styles.section} aria-labelledby="topics-title">
              <header className={styles.sectionHead}>
                <p className={styles.eyebrow}>Coverage</p>
                <h2 className={styles.sectionTitle} id="topics-title">
                  Topics
                </h2>
                <p className={styles.sectionIntro}>Sectors, technologies and interests to prioritise.</p>
              </header>

              <div className={styles.block}>
                <div className={styles.blockHead}>
                  <p className={styles.label}>Industries</p>
                  <span className={styles.counter}>
                    {preferences.industries.length}/{LIMITS.industries}
                  </span>
                </div>
                <PreferencePicker
                  id="industries"
                  addLabel="+ Add industry"
                  searchLabel="Add industry"
                  values={preferences.industries}
                  options={INDUSTRY_OPTIONS}
                  suggestions={SUGGESTED_INDUSTRIES}
                  limit={LIMITS.industries}
                  placeholder="Search industries"
                  onChange={(next) => setList('industries', next, LIMITS.industries)}
                  onLimit={() => handleLimit(LIMITS.industries)}
                />
              </div>

              <div className={styles.block}>
                <div className={styles.blockHead}>
                  <p className={styles.label}>Technologies</p>
                  <span className={styles.counter}>
                    {preferences.technologies.length}/{LIMITS.technologies}
                  </span>
                </div>
                <PreferencePicker
                  id="technologies"
                  addLabel="+ Add technology"
                  searchLabel="Add technology"
                  values={preferences.technologies}
                  options={TECHNOLOGY_OPTIONS}
                  suggestions={SUGGESTED_TECHNOLOGIES}
                  limit={LIMITS.technologies}
                  placeholder="Search technologies"
                  onChange={(next) => setList('technologies', next, LIMITS.technologies)}
                  onLimit={() => handleLimit(LIMITS.technologies)}
                />
              </div>

              <div className={styles.block}>
                <div className={styles.blockHead}>
                  <p className={styles.label}>Interests</p>
                  <span className={styles.counter}>
                    {interestTags.length}/{LIMITS.interestTags}
                  </span>
                </div>
                <p className={styles.hint}>Themes or phrases that should surface when they appear in coverage.</p>
                <PreferencePicker
                  id="interests"
                  addLabel="+ Add interest"
                  searchLabel="Add interest"
                  values={interestTags}
                  options={[]}
                  suggestions={SUGGESTED_INTERESTS}
                  allowCustom
                  limit={LIMITS.interestTags}
                  placeholder="Type a topic"
                  emptyMessage="Type a topic and press Enter"
                  onChange={(next) =>
                    applyPreferences((prev) => ({ ...prev, customInterests: serializeInterestTags(next) }))
                  }
                  onLimit={() => handleLimit(LIMITS.interestTags)}
                />
              </div>
            </section>

            <section className={styles.section} aria-labelledby="orgs-title">
              <header className={styles.sectionHead}>
                <p className={styles.eyebrow}>Who you follow</p>
                <h2 className={styles.sectionTitle} id="orgs-title">
                  Organisations
                </h2>
                <p className={styles.sectionIntro}>
                  Raise organisations in coverage, or monitor a shorter list for important developments.
                </p>
              </header>

              <div className={styles.block}>
                <div className={styles.blockHead}>
                  <p className={styles.label}>Organisations of interest</p>
                  <span className={styles.counter}>
                    {preferences.companies.length}/{LIMITS.companies}
                  </span>
                </div>
                  <p className={styles.hint}>Rank these higher in your briefing. This does not send email alerts.</p>
                <PreferencePicker
                  id="companies"
                  addLabel="+ Add organisation"
                  searchLabel="Add organisation of interest"
                  values={preferences.companies}
                  options={[]}
                  suggestions={SUGGESTED_ORGANISATIONS}
                  allowCustom
                  limit={LIMITS.companies}
                  placeholder="Type an organisation"
                  emptyMessage="Type a name and press Enter"
                  onChange={(next) => setList('companies', next, LIMITS.companies)}
                  onLimit={() => handleLimit(LIMITS.companies)}
                />
                {preferences.companies.length > 0 ? (
                  <div className={styles.monitorRow}>
                    {preferences.companies
                      .filter((name) => !watchlistLabels.has(name.toLowerCase()))
                      .slice(0, 4)
                      .map((name) => (
                        <button
                          key={name}
                          type="button"
                          className={styles.textAction}
                          disabled={watchlistSaving}
                          onClick={() => handleMonitorOrganisation(name)}
                        >
                          Monitor {name}
                        </button>
                      ))}
                  </div>
                ) : null}
              </div>

              <div className={styles.block}>
                <p className={styles.label}>Company scale</p>
                <p className={styles.hint}>Optional. Leave blank if scale does not matter. Velox will not exclude the rest.</p>
                <div className={styles.scaleGrid}>
                  {COMPANY_SCALE_OPTIONS.map((option) => {
                    const on = preferences.businessStages.includes(option.value);
                    return (
                      <button
                        key={option.value}
                        type="button"
                        className={`${styles.scaleChip} ${on ? styles.scaleChipOn : ''}`}
                        aria-pressed={on}
                        aria-label={option.label}
                        onClick={() => {
                          const current = preferences.businessStages;
                          const next = on ? current.filter((v) => v !== option.value) : [...current, option.value];
                          setList('businessStages', next, LIMITS.businessStages);
                        }}
                      >
                        <span className={styles.scaleLabel}>{option.label}</span>
                        <span className={styles.scaleHint}>{option.hint}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className={styles.block}>
                <div className={styles.blockHead}>
                  <p className={styles.label}>Watchlist</p>
                  <span className={styles.counter}>
                    {watchlistItems.length}/{watchlistMax}
                  </span>
                </div>
                <p className={styles.hint}>
                  Actively monitor these organisations for material developments. Important-move emails use this list.
                </p>
                {watchlistLoading ? (
                  <p className={styles.empty}>Loading watchlist…</p>
                ) : (
                  <>
                    <PreferencePicker
                      id="watchlist"
                      addLabel="+ Add organisation to monitor"
                      searchLabel="Add watchlist organisation"
                      values={watchlistItems.map((item) => item.label)}
                      options={[]}
                      suggestions={SUGGESTED_WATCHLIST}
                      allowCustom
                      limit={watchlistMax}
                      disabled={watchlistSaving}
                      placeholder="Type an organisation"
                      emptyMessage="Type a name and press Enter"
                      onChange={async (next) => {
                        const current = watchlistItems.map((item) => item.label);
                        const removed = watchlistItems.filter((item) => !next.includes(item.label));
                        const added = next.filter((label) => !current.includes(label));
                        if (removed.length) {
                          await Promise.all(removed.map((item) => handleRemoveWatchlist(item.id)));
                        }
                        if (added.length) {
                          await handleAddWatchlist(added[added.length - 1]);
                        }
                      }}
                      onLimit={() => handleLimit(watchlistMax)}
                    />
                    <p className={styles.watchSummary}>{formatWatchlistSummary(watchlistItems)}</p>
                    {hasLegacyWatchItems ? (
                      <p className={styles.hint}>
                        Older theme or keyword items are still monitored. New entries are organisations only. Use Interests for topics.
                      </p>
                    ) : null}
                  </>
                )}
              </div>
            </section>

            <section className={styles.section} aria-labelledby="markets-title">
              <header className={styles.sectionHead}>
                <p className={styles.eyebrow}>Market focus</p>
                <h2 className={styles.sectionTitle} id="markets-title">
                  Markets
                </h2>
                <p className={styles.sectionIntro}>
                  Countries and regions that should influence coverage. Independent of where you want events.
                </p>
              </header>
              <div className={styles.block}>
                <div className={styles.blockHead}>
                  <p className={styles.label}>Markets I follow</p>
                  <span className={styles.counter}>
                    {(loc.preferredCountries || []).length}/{LIMITS.preferredCountries}
                  </span>
                </div>
                <PreferencePicker
                  id="markets"
                  addLabel="+ Add market"
                  searchLabel="Add market"
                  values={loc.preferredCountries || []}
                  options={MARKET_OPTIONS}
                  suggestions={SUGGESTED_MARKETS}
                  limit={LIMITS.preferredCountries}
                  placeholder="Search countries or regions"
                  onChange={(next) =>
                    applyPreferences((prev) => ({
                      ...prev,
                      locationPreferences: { ...prev.locationPreferences, preferredCountries: next },
                    }))
                  }
                  onLimit={() => handleLimit(LIMITS.preferredCountries)}
                />
                <div className={styles.deliveryRow}>
                  <div className={styles.deliveryCopy}>
                    <p className={styles.deliveryTitle} id="current-market-label">
                      Use my current location
                    </p>
                    <p className={styles.hint}>Automatically include the country you're currently in.</p>
                    {loc.followCurrentMarket && loc.currentMarketCountry ? (
                      <p className={styles.currentMarketQuiet}>Current: {loc.currentMarketCountry}</p>
                    ) : null}
                    {marketGeoStatus === 'requesting' ? (
                      <p className={styles.hint}>Finding your country…</p>
                    ) : marketGeoStatus === 'denied' ? (
                      <p className={styles.hint}>Location access was blocked. Last known country is kept until you update it.</p>
                    ) : marketGeoStatus === 'unavailable' ? (
                      <p className={styles.hint}>Current location is not available in this browser.</p>
                    ) : marketGeoStatus === 'error' || marketGeoStatus === 'network' || marketGeoStatus === 'auth' ? (
                      <p className={styles.hint}>Could not update your current country. Last known country is kept if we have one.</p>
                    ) : null}
                    {loc.followCurrentMarket ? (
                      <button
                        type="button"
                        className={styles.textAction}
                        onClick={handleRefreshCurrentMarket}
                        disabled={marketGeoStatus === 'requesting'}
                      >
                        {marketGeoStatus === 'requesting' ? 'Updating country…' : 'Update current country'}
                      </button>
                    ) : null}
                  </div>
                  <Switch
                    checked={loc.followCurrentMarket === true}
                    onChange={handleFollowCurrentMarketToggle}
                    disabled={marketGeoStatus === 'requesting'}
                    labelledBy="current-market-label"
                  />
                </div>
              </div>
            </section>

            <section className={styles.section} aria-labelledby="events-title">
              <header className={styles.sectionHead}>
                <p className={styles.eyebrow}>In person and online</p>
                <h2 className={styles.sectionTitle} id="events-title">
                  Events
                </h2>
                <p className={styles.sectionIntro}>Where you can attend, not the markets you follow.</p>
              </header>

              <div className={styles.block}>
                <div className={styles.blockHead}>
                  <p className={styles.label}>Event types</p>
                </div>
                <PreferencePicker
                  id="event-types"
                  addLabel="+ Add event type"
                  searchLabel="Add event type"
                  values={preferences.eventTypes}
                  options={EVENT_TYPE_OPTIONS}
                  suggestions={SUGGESTED_EVENT_TYPES}
                  limit={LIMITS.eventTypes}
                  placeholder="Search event types"
                  onChange={(next) => setList('eventTypes', next, LIMITS.eventTypes)}
                  onLimit={() => handleLimit(LIMITS.eventTypes)}
                />
              </div>

              <div className={styles.block}>
                <div className={styles.blockHead}>
                  <p className={styles.label} id="event-location-label">
                    Event location
                  </p>
                </div>
                <div className={styles.cadence} role="radiogroup" aria-labelledby="event-location-label">
                  <button
                    type="button"
                    role="radio"
                    aria-checked={useCurrentLocation}
                    className={useCurrentLocation ? styles.cadenceActive : styles.cadenceBtn}
                    onClick={() => handleEventPlaceMode(true)}
                  >
                    Near me
                  </button>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={!useCurrentLocation}
                    className={!useCurrentLocation ? styles.cadenceActive : styles.cadenceBtn}
                    onClick={() => handleEventPlaceMode(false)}
                  >
                    Choose location
                  </button>
                </div>
                {useCurrentLocation ? (
                  <>
                    <p className={styles.hint}>{geoHint}</p>
                    <button
                      type="button"
                      className={styles.textAction}
                      onClick={() => requestCurrentLocation({ force: true })}
                      disabled={geoStatus === 'requesting'}
                    >
                      {geoStatus === 'requesting' ? 'Updating location…' : 'Update current location'}
                    </button>
                  </>
                ) : null}

                {!useCurrentLocation ? (
                  <>
                    <div className={styles.selectors}>
                      <div className={styles.field}>
                        <p className={styles.fieldLabel} id="event-country-label">
                          Country
                        </p>
                        <PreferencePicker
                          id="event-country"
                          mode="single"
                          value={overrideCountry}
                          options={COUNTRY_OPTIONS}
                          placeholder="Select a country"
                          searchLabel="Event country"
                          onSelect={(country) =>
                            applyPreferences((prev) => ({
                              ...prev,
                              locationPreferences: {
                                ...prev.locationPreferences,
                                locationOverride: { country, city: '' },
                              },
                            }))
                          }
                        />
                      </div>
                      <div className={styles.field}>
                        <p className={styles.fieldLabel} id="event-city-label">
                          City
                        </p>
                        <PreferencePicker
                          id="event-city"
                          mode="single"
                          value={overrideCity}
                          options={cityChoices}
                          allowCustom
                          placeholder="Search or type a city"
                          searchLabel="Event city"
                          emptyMessage="Type a city and press Enter"
                          onSelect={(city) =>
                            applyPreferences((prev) => ({
                              ...prev,
                              locationPreferences: {
                                ...prev.locationPreferences,
                                locationOverride: {
                                  ...prev.locationPreferences.locationOverride,
                                  city,
                                },
                              },
                            }))
                          }
                        />
                      </div>
                    </div>
                    {overrideCountry ? (
                      <p className={styles.preview}>
                        Events will favour <strong>{chosenLabel || overrideCountry}</strong>.
                      </p>
                    ) : null}
                  </>
                ) : null}
              </div>
            </section>

            <section className={styles.section} aria-labelledby="delivery-title">
              <header className={styles.sectionHead}>
                <p className={styles.eyebrow}>How Velox delivers it</p>
                <h2 className={styles.sectionTitle} id="delivery-title">
                  Briefings
                </h2>
                <p className={styles.sectionIntro}>Email for the intelligence configured on this page. Other account emails stay in Notifications.</p>
              </header>

              <div className={styles.deliveryRow}>
                <div className={styles.deliveryCopy}>
                  <p className={styles.deliveryTitle} id="weekly-briefing-label">
                    Weekly Velox briefing
                  </p>
                  <p className={styles.hint}>
                    A Sunday scan using your full profile: topics, organisations, watchlist and markets. Typically evening UK time.
                  </p>
                </div>
                <Switch
                  checked={weeklyDigestEmailEnabled}
                  onChange={handleWeeklyDigestEmailToggle}
                  disabled={weeklyEmailSaving}
                  labelledBy="weekly-briefing-label"
                />
              </div>

              <div className={styles.deliveryRow}>
                <div className={styles.deliveryCopy}>
                  <p className={styles.deliveryTitle} id="news-alerts-label">
                    Important-move emails
                  </p>
                  <p className={styles.hint}>
                    Occasional alerts when something material happens around organisations on your Watchlist, not every mention. Capped daily. Topics and organisations of interest do not trigger these emails.
                  </p>
                </div>
                <Switch
                  checked={newsAlertsEmailEnabled}
                  onChange={handleNewsAlertsEmailToggle}
                  disabled={newsAlertsSaving}
                  labelledBy="news-alerts-label"
                />
              </div>

              <Link className={styles.moreLink} to="/notification-settings">
                Other email settings
              </Link>
            </section>
          </>
        )}
      </div>
    </HubPageShell>
  );
}
